import { useRef, useState, useLayoutEffect } from "react";
import { cn } from "@/lib/utils";
import { Link as LinkIcon, ExternalLink } from "lucide-react";

const inputCls =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-colors";

// Build a regex that matches "@Full Name" for any known user name.
function mentionRegex(users) {
  const names = (users || []).map((u) => u.name).filter(Boolean).sort((a, b) => b.length - a.length);
  if (!names.length) return null;
  const esc = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(`@(${esc.join("|")})`, "g");
}

const URL_RE = /\b(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

function LinkChip({ url }) {
  const href = url.startsWith("http") ? url : `https://${url}`;
  let host = url, rest = "";
  try {
    const u = new URL(href);
    host = u.hostname.replace(/^www\./, "");
    rest = `${u.pathname === "/" ? "" : u.pathname}${u.search}`;
  } catch { /* keep raw */ }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" title={href} onClick={(e) => e.stopPropagation()} data-testid="link-chip"
      className="mx-0.5 inline-flex max-w-[16rem] items-center gap-1 rounded-md bg-white/90 px-1.5 py-0.5 align-middle text-xs font-medium text-sky-700 no-underline ring-1 ring-black/5 transition-colors hover:bg-white hover:text-sky-900">
      <LinkIcon className="h-3 w-3 shrink-0" />
      <span className="shrink-0 font-semibold">{host}</span>
      {rest && <span className="min-w-0 truncate text-sky-600/70">{rest}</span>}
      <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
    </a>
  );
}

function linkify(str, keyBase) {
  const out = [];
  let last = 0, m, i = 0;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(str)) !== null) {
    let url = m[0];
    const trail = url.match(/[.,!?;:)'"\]]+$/);
    if (trail) url = url.slice(0, -trail[0].length);
    if (m.index > last) out.push(str.slice(last, m.index));
    out.push(<LinkChip key={`${keyBase}-l${i++}`} url={url} />);
    last = m.index + url.length;
    URL_RE.lastIndex = last;
  }
  if (last < str.length) out.push(str.slice(last));
  return out;
}

export function renderWithMentions(text, users) {
  if (!text) return text;
  const re = mentionRegex(users);
  const parts = [];
  let last = 0, m, i = 0;
  if (re) {
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(...linkify(text.slice(last, m.index), i));
      parts.push(<span key={`m${i++}`} className="rounded bg-violet-50 px-1 font-medium text-violet-700">@{m[1]}</span>);
      last = m.index + m[0].length;
    }
  }
  if (last < text.length) parts.push(...linkify(text.slice(last), `e${i}`));
  return parts;
}

export function MentionTextarea({ label, value, onChange, users = [], placeholder, rows = 2, testid, onBlur, className, onEnter, menuUp = false, autoGrow = false, maxRows = 5 }) {
  const ref = useRef();
  const [menu, setMenu] = useState(null); // { query, from } | null
  const [active, setActive] = useState(0);

  useLayoutEffect(() => {
    if (!autoGrow) return;
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      el.style.height = "auto";
      const cs = window.getComputedStyle(el);
      const lh = parseFloat(cs.lineHeight) || 20;
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const minH = lh * rows + padY;
      const maxH = lh * maxRows + padY;
      // Empty boxes stay at their row height (placeholder wrapping in narrow windows shouldn't grow them)
      const next = el.value ? Math.max(minH, Math.min(el.scrollHeight, maxH)) : minH;
      el.style.height = `${next}px`;
      el.style.overflowY = el.value && el.scrollHeight > maxH + 1 ? "auto" : "hidden";
    };
    fit();
    let w = el.clientWidth;
    const ro = new ResizeObserver(() => { if (el.clientWidth !== w) { w = el.clientWidth; fit(); } });
    ro.observe(el);
    return () => ro.disconnect();
  }, [value, autoGrow, maxRows, rows]);

  const detect = (el) => {
    const pos = el.selectionStart;
    const before = (value || "").slice(0, pos);
    const at = before.lastIndexOf("@");
    if (at === -1) return setMenu(null);
    const between = before.slice(at + 1);
    // only trigger while typing the first token (letters/space allowed but stop at newline)
    if (/[\n]/.test(between) || between.length > 30) return setMenu(null);
    setActive(0);
    setMenu({ query: between.toLowerCase(), from: at });
  };

  const matches = menu
    ? users.filter((u) => u.name && u.name.toLowerCase().includes(menu.query)).slice(0, 6)
    : [];

  const pick = (u) => {
    const pos = ref.current?.selectionStart ?? (value || "").length;
    const next = (value || "").slice(0, menu.from) + `@${u.name} ` + (value || "").slice(pos);
    onChange(next);
    setMenu(null);
    setTimeout(() => ref.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (menu && matches.length > 0) {
      if (e.key === "Tab" || e.key === "Enter") {
        e.preventDefault();
        pick(matches[Math.min(active, matches.length - 1)]);
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => (i + 1) % matches.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => (i - 1 + matches.length) % matches.length);
      } else if (e.key === "Escape") {
        setMenu(null);
      }
      return;
    }
    if (onEnter && e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      onEnter();
    }
  };

  return (
    <div className="relative">
      {label && <label className="mb-1.5 block text-xs font-semibold text-slate-600">{label}</label>}
      <textarea
        ref={ref}
        data-testid={testid}
        rows={rows}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); detect(e.target); }}
        onKeyUp={(e) => { if (!["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(e.key)) detect(e.currentTarget); }}
        onKeyDown={onKeyDown}
        onClick={(e) => detect(e.currentTarget)}
        onBlur={() => { setTimeout(() => setMenu(null), 150); onBlur && onBlur(); }}
        className={cn(inputCls, autoGrow ? "resize-none" : "resize-y", className)}
      />
      {menu && matches.length > 0 && (
        <div className={cn("absolute z-50 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg", menuUp ? "bottom-full mb-1" : "mt-1")} data-testid={`${testid}-mention-menu`}>
          {matches.map((u, idx) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              onMouseEnter={() => setActive(idx)}
              data-testid={`${testid}-mention-${u.id}`}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-sky-50",
                idx === active && "bg-sky-50"
              )}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-100 text-[10px] font-bold text-violet-700">
                {u.name?.[0]?.toUpperCase()}
              </span>
              {u.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
